import { useEffect, useState } from "react";
import { Plus, KeyRound, Eye, XCircle, ShieldAlert } from "lucide-react";
import { api, formatApiError, formatTL } from "@/lib/api";
import { ACCESS_STATUS, SCOPE_LABEL } from "@/lib/dataAccess";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const dt = (s) => (s ? new Date(s).toLocaleString("tr-TR") : "-");
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

const RecordsTable = ({ data }) => {
  const cols = data.scope === "PORTFOLIO"
    ? [["title", "Başlık"], ["property_type", "Tip"], ["city", "İl"], ["district", "İlçe"], ["price", "Fiyat"], ["status", "Durum"]]
    : [["full_name", "Ad (maskeli)"], ["phone", "Telefon (maskeli)"], ["customer_type", "Tip"], ["budget_max", "Maks. Bütçe"]];
  return (
    <table className="w-full text-sm" data-testid="access-records-table">
      <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>{cols.map(([, l]) => <th key={l} className="text-left px-3 py-2">{l}</th>)}</tr></thead>
      <tbody>
        {data.records.map((r, i) => (
          <tr key={i} className="border-t">{cols.map(([k]) => <td key={k} className="px-3 py-2">{["price", "budget_max"].includes(k) ? (r[k] ? formatTL(r[k]) : "-") : r[k] ?? "-"}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
};

export default function DataAccessAdminPage() {
  const [items, setItems] = useState([]);
  const [offices, setOffices] = useState([]);
  const [form, setForm] = useState(null);
  const [unlockFor, setUnlockFor] = useState(null);
  const [code, setCode] = useState("");
  const [view, setView] = useState(null);

  const load = async () => setItems((await api.get("/admin/data-access/requests")).data);
  useEffect(() => { load(); api.get("/offices").then(({ data }) => setOffices(data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/data-access/requests", { ...form, duration_hours: Number(form.duration_hours) });
      toast.success("Talep ofise gönderildi");
      setForm(null);
      load();
    } catch (e2) { err(e2); }
  };

  const unlock = async () => {
    try {
      await api.post(`/admin/data-access/requests/${unlockFor.id}/unlock`, { code });
      toast.success("Erişim açıldı");
      setUnlockFor(null);
      setCode("");
      load();
    } catch (e2) { err(e2); }
  };

  const openData = async (r) => {
    try { setView({ req: r, ...(await api.get(`/admin/data-access/requests/${r.id}/data`)).data }); load(); } catch (e2) { err(e2); load(); }
  };

  const close = async (r) => { await api.post(`/admin/data-access/requests/${r.id}/close`); load(); };

  return (
    <div className="space-y-6" data-testid="data-access-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Süper Admin</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Sınırlı Veri Erişimi</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">Ofis kayıtlarına doğrudan erişim yoktur. Gerekli durumda talep oluşturun; ofis broker'ı onaylayınca size ileteceği tek kullanımlık kodu girin. Erişim süreli, tek kapsamlı ve en fazla 50 kayıtla sınırlıdır; müşteri bilgileri maskelenir ve her görüntüleme kayda geçer.</p>
        </div>
        <Button data-testid="new-access-request-btn" onClick={() => setForm({ office_id: "", scope: "PORTFOLIO", reason: "", duration_hours: 2 })} className="bg-slate-900"><Plus className="w-4 h-4 mr-2" />Erişim Talebi</Button>
      </div>

      <Card className="divide-y">
        {items.length === 0 && <div className="p-10 text-center text-slate-400">Henüz talep yok.</div>}
        {items.map((r) => (
          <div key={r.id} data-testid={`access-row-${r.id}`} className="p-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-semibold">{r.office_name} <span className="font-mono text-xs text-slate-500">{r.office_code}</span></div>
              <div className="text-sm text-slate-600 mt-0.5">{SCOPE_LABEL[r.scope]} · {r.duration_hours} saat · {r.reason}</div>
              <div className="text-xs text-slate-500 mt-1">Talep: {dt(r.created_at)}{r.access_expires_at && ` · Bitiş: ${dt(r.access_expires_at)}`} · Görüntüleme: {r.view_count}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" data-testid={`access-status-${r.id}`} className={ACCESS_STATUS[r.status]?.cls}>{ACCESS_STATUS[r.status]?.label}</Badge>
              {r.status === "APPROVED" && <Button size="sm" data-testid={`enter-code-${r.id}`} onClick={() => setUnlockFor(r)} className="bg-blue-600 hover:bg-blue-700"><KeyRound className="w-3.5 h-3.5 mr-1" />Kod Gir</Button>}
              {r.status === "ACTIVE" && <Button size="sm" data-testid={`view-data-${r.id}`} onClick={() => openData(r)} className="bg-emerald-600 hover:bg-emerald-700"><Eye className="w-3.5 h-3.5 mr-1" />Görüntüle</Button>}
              {["PENDING", "APPROVED", "ACTIVE"].includes(r.status) && <Button size="sm" variant="outline" data-testid={`close-access-${r.id}`} onClick={() => close(r)}><XCircle className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Sınırlı Veri Erişim Talebi</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label>Ofis *</Label>
                <Select value={form.office_id} onValueChange={(v) => setForm({ ...form, office_id: v })}>
                  <SelectTrigger data-testid="access-office-select"><SelectValue placeholder="Ofis seçin" /></SelectTrigger>
                  <SelectContent>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.office_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kapsam</Label>
                  <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                    <SelectTrigger data-testid="access-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="PORTFOLIO">Portföy kayıtları</SelectItem><SelectItem value="CUSTOMER">Müşteri kayıtları (maskeli)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Süre (saat, 1-24)</Label><Input data-testid="access-duration" type="number" min="1" max="24" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} /></div>
              </div>
              <div><Label>Gerekçe * (en az 10 karakter)</Label><Textarea data-testid="access-reason" required rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>İptal</Button>
                <Button data-testid="access-submit-btn" type="submit" disabled={!form.office_id} className="bg-slate-900">Talep Gönder</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!unlockFor} onOpenChange={(o) => !o && setUnlockFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Erişim Kodunu Girin</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{unlockFor?.office_name} broker'ının size ilettiği 6 haneli tek kullanımlık kod (30 dk geçerli, 5 deneme hakkı).</p>
          <Input data-testid="access-code-input" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="font-mono text-center text-lg tracking-[0.4em]" placeholder="000000" />
          <Button data-testid="access-code-submit" onClick={unlock} disabled={code.length !== 6} className="bg-slate-900">Erişimi Aç</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{view?.req.office_name} — {SCOPE_LABEL[view?.scope]}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <ShieldAlert className="w-4 h-4" /> Sınırlı görünüm: en fazla {view.max_records} kayıt{view.masked && ", kişisel bilgiler maskeli"}. Erişim {dt(view.access_expires_at)} tarihinde kapanır. Bu görüntüleme kayda geçti.
              </div>
              {view.records.length === 0 ? <div className="text-center text-slate-400 py-6">Kayıt yok.</div> : <RecordsTable data={view} />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
